"use client";

import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Chip,
  Alert,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import HistoryIcon from "@mui/icons-material/History";

const WalletManagement = ({ businessId }) => {
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showDebitModal, setShowDebitModal] = useState(false);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Dummy wallet data
  const dummyWallet = {
    businessId: businessId || "BUS-0001",
    balance: 5420,
    totalCredits: 10000,
    totalDebits: 4580,
    currency: "INR",
    lowBalanceThreshold: 1000,
    isActive: true,
    lastTransactionDate: new Date(),
  };

  //  Dummy transactions
  const dummyTransactions = [
    {
      _id: "1",
      transactionId: "TXN123456789",
      type: "CREDIT",
      category: "PLAN_PURCHASE",
      amount: 5000,
      balanceBefore: 420,
      balanceAfter: 5420,
      description: "Plan Purchase - Growth Plan",
      status: "SUCCESS",
      createdAt: new Date("2025-03-15"),
    },
    {
      _id: "2",
      transactionId: "TXN123456788",
      type: "DEBIT",
      category: "MESSAGE_COST",
      amount: 150,
      balanceBefore: 570,
      balanceAfter: 420,
      description: "Marketing Message - 50 messages",
      metadata: {
        messageCategory: "MARKETING",
        messageCount: 50,
      },
      status: "SUCCESS",
      createdAt: new Date("2025-03-14"),
    },
    {
      _id: "3",
      transactionId: "TXN123456787",
      type: "DEBIT",
      category: "MESSAGE_COST",
      amount: 80,
      balanceBefore: 650,
      balanceAfter: 570,
      description: "Utility Message - 40 messages",
      metadata: {
        messageCategory: "UTILITY",
        messageCount: 40,
      },
      status: "SUCCESS",
      createdAt: new Date("2025-03-13"),
    },
  ];

  useEffect(() => {
    fetchWalletData();
    fetchTransactions();
  }, [businessId]);

  const fetchWalletData = async () => {
    try {
      // For now, using dummy data
      setWalletData(dummyWallet);

      // Uncomment when backend is ready
      // const response = await fetch(
      //   `${process.env.REACT_APP_API_URL}/api/admin/whatsapp/wallets/${businessId}`
      // );
      // const data = await response.json();
      // setWalletData(data);
    } catch (error) {
      console.error("Error fetching wallet data:", error);
      setWalletData(dummyWallet);
    }
  };

  const fetchTransactions = async () => {
    try {
      // For now, using dummy data
      setTransactions(dummyTransactions);

      // Uncomment when backend is ready
      // const response = await fetch(
      //   `${process.env.REACT_APP_API_URL}/api/admin/whatsapp/transactions/business/${businessId}?limit=10`
      // );
      // const data = await response.json();
      // setTransactions(data.transactions || []);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions(dummyTransactions);
    }
  };

  const handleCreditWallet = async () => {
    if (!amount || amount <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    setLoading(true);
    setError("");
    try {
      // For now, just update dummy data
      const newBalance = walletData.balance + Number(amount);
      setWalletData({
        ...walletData,
        balance: newBalance,
        totalCredits: walletData.totalCredits + Number(amount),
      });
      setShowCreditModal(false);
      setAmount("");
      setDescription("");
      fetchTransactions();

      // Uncomment when backend is ready
      // const response = await fetch(
      //   `${process.env.REACT_APP_API_URL}/api/admin/whatsapp/wallets/credit`,
      //   {
      //     method: "POST",
      //     headers: { "Content-Type": "application/json" },
      //     body: JSON.stringify({
      //       businessId,
      //       amount: Number(amount),
      //       description,
      //       category: "MANUAL_CREDIT",
      //     }),
      //   }
      // );
      // fetchWalletData();
      // fetchTransactions();
    } catch (error) {
      setError("Failed to credit wallet");
      console.error("Error crediting wallet:", error);
    }
    setLoading(false);
  };

  const handleDebitWallet = async () => {
    if (!amount || amount <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    if (walletData.balance < Number(amount)) {
      setError("Insufficient balance");
      return;
    }

    setLoading(true);
    setError("");
    try {
      // For now, just update dummy data
      const newBalance = walletData.balance - Number(amount);
      setWalletData({
        ...walletData,
        balance: newBalance,
        totalDebits: walletData.totalDebits + Number(amount),
      });
      setShowDebitModal(false);
      setAmount("");
      setDescription("");
      fetchTransactions();

      // Uncomment when backend is ready
      // const response = await fetch(
      //   `${process.env.REACT_APP_API_URL}/api/admin/whatsapp/wallets/debit`,
      //   {
      //     method: "POST",
      //     headers: { "Content-Type": "application/json" },
      //     body: JSON.stringify({
      //       businessId,
      //       amount: Number(amount),
      //       description,
      //       category: "ADJUSTMENT",
      //     }),
      //   }
      // );
      // fetchWalletData();
      // fetchTransactions();
    } catch (error) {
      setError("Failed to debit wallet");
      console.error("Error debiting wallet:", error);
    }
    setLoading(false);
  };

  if (!walletData) {
    return (
      <Card>
        <CardContent>
          <Typography>Loading wallet data...</Typography>
        </CardContent>
      </Card>
    );
  }

  const isLowBalance = walletData.balance < walletData.lowBalanceThreshold;

  return (
    <Box>
      {isLowBalance && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Low Balance Alert: Your wallet balance is below the threshold of ₹
          {walletData.lowBalanceThreshold}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Wallet Balance Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <AccountBalanceWalletIcon color="primary" />
                <Typography variant="h6" ml={1}>
                  Current Balance
                </Typography>
              </Box>
              <Typography variant="h4" color="primary" fontWeight="bold">
                ₹{walletData.balance.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="textSecondary" mt={1}>
                Last updated:{" "}
                {new Date(walletData.lastTransactionDate).toLocaleString()}
              </Typography>
              <Box mt={2} display="flex" gap={1}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<AddCircleIcon />}
                  onClick={() => setShowCreditModal(true)}
                >
                  Credit
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<RemoveCircleIcon />}
                  onClick={() => setShowDebitModal(true)}
                >
                  Debit
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Credits Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Credits
              </Typography>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                ₹{walletData.totalCredits.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="textSecondary" mt={1}>
                All-time credits
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Debits Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Debits
              </Typography>
              <Typography variant="h4" color="error.main" fontWeight="bold">
                ₹{walletData.totalDebits.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="textSecondary" mt={1}>
                All-time debits
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Transaction History */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <HistoryIcon color="primary" />
                <Typography variant="h6" ml={1}>
                  Recent Transactions
                </Typography>
              </Box>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Transaction ID</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Balance</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((txn) => (
                      <TableRow key={txn._id}>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {txn.transactionId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={txn.type}
                            size="small"
                            color={txn.type === "CREDIT" ? "success" : "error"}
                          />
                        </TableCell>
                        <TableCell>{txn.category}</TableCell>
                        <TableCell>
                          <Typography
                            color={
                              txn.type === "CREDIT"
                                ? "success.main"
                                : "error.main"
                            }
                            fontWeight="bold"
                          >
                            {txn.type === "CREDIT" ? "+" : "-"}₹{txn.amount}
                          </Typography>
                        </TableCell>
                        <TableCell>₹{txn.balanceAfter}</TableCell>
                        <TableCell>{txn.description}</TableCell>
                        <TableCell>
                          {new Date(txn.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={txn.status}
                            size="small"
                            color={
                              txn.status === "SUCCESS" ? "success" : "default"
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Credit Modal */}
      <Dialog
        open={showCreditModal}
        onClose={() => {
          setShowCreditModal(false);
          setAmount("");
          setDescription("");
          setError("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Credit Wallet</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <TextField
              label="Amount (₹) *"
              fullWidth
              margin="normal"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <TextField
              label="Description"
              fullWidth
              margin="normal"
              multiline
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            {error && (
              <Typography color="error" mt={2}>
                {error}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowCreditModal(false);
              setAmount("");
              setDescription("");
              setError("");
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleCreditWallet}
            disabled={loading}
          >
            Credit Wallet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Debit Modal */}
      <Dialog
        open={showDebitModal}
        onClose={() => {
          setShowDebitModal(false);
          setAmount("");
          setDescription("");
          setError("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Debit Wallet</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Current Balance: ₹{walletData.balance.toFixed(2)}
            </Alert>
            <TextField
              label="Amount (₹) *"
              fullWidth
              margin="normal"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <TextField
              label="Description"
              fullWidth
              margin="normal"
              multiline
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            {error && (
              <Typography color="error" mt={2}>
                {error}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowDebitModal(false);
              setAmount("");
              setDescription("");
              setError("");
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDebitWallet}
            disabled={loading}
          >
            Debit Wallet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WalletManagement;
